/**
 * Corte esquemático do poço com a bomba submersa — Nexora Grid (frontend, retorno de 18/10/2026).
 *
 * Criado em: 18/10/2026 · Última modificação: 18/10/2026 · Versão: 1.0.0
 *
 * Desenha, a partir da ficha (NBR 12212 / NBR 12244, profundidades a partir da boca), o cavalete na superfície, o
 * revestimento, a água entre o nível estático e o fundo, o nível dinâmico, a seção filtrante e a bomba no crivo,
 * com a submergência (crivo − ND). Escala vertical proporcional à profundidade do poço.
 */

type Poco = Partial<Record<"profundidade_poco_m" | "nivel_estatico_m" | "nivel_dinamico_m" | "profundidade_instalacao_m" | "topo_filtros_m", number>>;

const fmt = (v: number) => `${v.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} m`;

export function CortePoco({ poco }: { poco: Poco }) {
  const fundo = poco.profundidade_poco_m;
  if (!fundo) return <p className="nx-suave">Informe a profundidade do poço para ver o corte.</p>;
  const L = 300, A = 276, topo = 34, base = A - 30, cx = 95, r = 14;
  const y = (m: number) => topo + (Math.min(m, fundo) / fundo) * (base - topo);
  const { nivel_estatico_m: ne, nivel_dinamico_m: nd, profundidade_instalacao_m: crivo, topo_filtros_m: filtros } = poco;
  const marcas: [number | undefined, string, string][] = [[ne, "NE", "#4f8fe6"], [nd, "ND", "#22d3ee"], [crivo, "bomba", "#c084fc"], [filtros, "filtros", "#94a3b8"]];
  // rótulos sem sobreposição: no mínimo 13 px entre um e outro (a linha liga o rótulo à profundidade)
  const posicoes = marcas.filter((m): m is [number, string, string] => m[0] !== undefined).sort((a, b) => a[0] - b[0])
    .reduce<{ v: number; rotulo: string; cor: string; ty: number }[]>((acc, [v, rotulo, cor]) => {
      const anterior = acc[acc.length - 1];
      acc.push({ v, rotulo, cor, ty: Math.max(y(v), anterior ? anterior.ty + 13 : -Infinity) });
      return acc;
    }, []);
  return (
    <svg className="corte-poco" width={L} height={A} role="img"
      aria-label={`Corte do poço: fundo ${fmt(fundo)}${ne !== undefined ? `, NE ${fmt(ne)}` : ""}${nd !== undefined ? `, ND ${fmt(nd)}` : ""}${crivo !== undefined ? `, bomba a ${fmt(crivo)}` : ""}`}>
      <text x={4} y={12} className="titulo">corte esquemático do poço</text>
      {/* terreno, laje do cavalete e saída da edutora */}
      <line x1={0} y1={topo} x2={L} y2={topo} className="terreno" />
      <rect x={cx - 26} y={topo - 6} width={52} height={6} className="laje" />
      <path d={`M${cx} ${topo - 6} V${topo - 20} H${cx + 60}`} className="edutor" />
      {/* revestimento e água (do NE ao fundo) */}
      <rect x={cx - r} y={topo} width={r * 2} height={base - topo} className="revestimento" />
      {ne !== undefined && <rect x={cx - r + 2} y={y(ne)} width={r * 2 - 4} height={base - y(ne)} className="agua" />}
      {filtros !== undefined && <rect x={cx - r} y={y(filtros)} width={r * 2} height={base - y(filtros)} className="filtros" />}
      {/* edutora e bomba (motor abaixo do corpo) */}
      {crivo !== undefined && <>
        <line x1={cx} y1={topo - 6} x2={cx} y2={y(crivo) - 18} className="edutor" />
        <rect x={cx - 6} y={y(crivo) - 18} width={12} height={18} rx={2} className="bomba" />
        <rect x={cx - 6} y={y(crivo)} width={12} height={14} rx={2} className="motor" />
      </>}
      {posicoes.map(({ v, rotulo, cor, ty }) => (
        <g key={rotulo}>
          <line x1={cx + r + 2} y1={y(v)} x2={cx + r + 30} y2={y(v)} stroke={cor} strokeDasharray={rotulo === "ND" ? "4 2" : undefined} />
          <line x1={cx + r + 30} y1={y(v)} x2={cx + r + 36} y2={ty} stroke={cor} />
          <text x={cx + r + 38} y={ty + 4} fill={cor}>{rotulo} {fmt(v)}</text>
        </g>
      ))}
      <text x={cx + r + 38} y={base + 4} className="rotulo">fundo {fmt(fundo)}</text>
      {crivo !== undefined && nd !== undefined && (
        <text x={4} y={A - 2} className={crivo - nd < 3 ? "alerta" : "rotulo"}>submergência {fmt(crivo - nd)} (recomendado ≥ 3 m)</text>
      )}
    </svg>
  );
}
